import baseApiSlice from 'api/baseApiSlice';

import { waitForCompleteAccountPropagation } from './completeAccountPropagation';
import {
  type CompleteAccountRequest,
  type User,
  type CreateUserRequest,
  type CurrentUser,
  type ListUsersRequest,
  type ListUsersResponse,
  type RemoveUserRequest,
  type ResendWelcomeEmailRequest,
  type TransferLegalRepRequest,
  type TransferLegalRepResponse,
  type UpdateUserRequest,
} from './types';

type UsersTagTypes = {
  CurrentUser: 'CurrentUser';
};

type UsersTypesConstraints<Type> = {
  [Property in keyof Type]: Type[Property];
};

export const UsersTags: UsersTypesConstraints<UsersTagTypes> = {
  CurrentUser: 'CurrentUser',
};

const usersAPISlice = baseApiSlice
  .enhanceEndpoints({
    addTagTypes: Object.values(UsersTags),
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getCurrentUser: build.query<CurrentUser, void>({
        query: () => ({
          url: '/charity/users/v1/me',
          method: 'POST',
        }),
        providesTags: [UsersTags.CurrentUser],
      }),
      listUsers: build.query<ListUsersResponse, ListUsersRequest>({
        query: (payload) => ({
          url: '/charity/users/v1/list',
          method: 'POST',
          data: payload,
        }),
      }),
      createUser: build.mutation<User, CreateUserRequest>({
        query: (payload) => ({
          url: '/charity/users/v1',
          method: 'POST',
          data: payload,
        }),
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            const { data: newUser } = await queryFulfilled;
            dispatch(
              usersAPISlice.util.updateQueryData('listUsers', {}, (draft) => {
                draft.push(newUser);
              }),
            );
          } catch {
            // pass
          }
        },
      }),
      updateUser: build.mutation<User, UpdateUserRequest & { isSelf?: boolean }>({
        query: ({ isSelf: _isSelf, ...payload }) => ({
          url: '/charity/users/v1/update',
          method: 'POST',
          data: payload,
        }),
        onQueryStarted: async ({ userId, isSelf }, { dispatch, queryFulfilled }) => {
          try {
            const { data: updatedUser } = await queryFulfilled;
            dispatch(
              usersAPISlice.util.updateQueryData('listUsers', {}, (draft) => {
                const index = draft.findIndex((u) => u.userId === userId);
                if (index !== -1) {
                  draft[index] = { ...draft[index], ...updatedUser };
                }
              }),
            );
            if (isSelf) {
              dispatch(
                usersAPISlice.util.updateQueryData('getCurrentUser', undefined, (draft) => {
                  draft.roles = [updatedUser.role];
                }),
              );
            }
          } catch {
            // pass
          }
        },
      }),
      transferLegalRep: build.mutation<TransferLegalRepResponse, TransferLegalRepRequest>({
        query: (payload) => ({
          url: '/charity/users/v1/transfer-legal-rep',
          method: 'POST',
          data: payload,
        }),
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            const {
              data: { newLegalRep, previousLegalRep },
            } = await queryFulfilled;
            dispatch(
              usersAPISlice.util.updateQueryData('listUsers', {}, (draft) => {
                [newLegalRep, previousLegalRep].forEach((member) => {
                  const index = draft.findIndex((u) => u.userId === member.userId);
                  if (index !== -1) {
                    draft[index] = { ...draft[index], role: member.role };
                  }
                });
              }),
            );
            // The caller is always the outgoing rep, so /me always changes.
            dispatch(
              usersAPISlice.util.updateQueryData('getCurrentUser', undefined, (draft) => {
                draft.roles = [previousLegalRep.role];
              }),
            );
          } catch {
            // pass
          }
        },
      }),
      resendWelcomeEmail: build.mutation<void, ResendWelcomeEmailRequest>({
        query: (payload) => ({
          url: '/charity/users/v1/resend-welcome-email',
          method: 'POST',
          data: payload,
        }),
      }),
      completeAccount: build.mutation<void, CompleteAccountRequest>({
        // Hold the mutation open for an extra moment after the request resolves
        // so /me has time to reflect the newly-set first/last name; the
        // submit button stays in its loading state for the whole window.
        queryFn: async (payload, _api, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: '/charity/users/v1/completeAccount',
            method: 'POST',
            data: payload,
          });
          if (result.error) {
            return { error: result.error };
          }
          await waitForCompleteAccountPropagation();
          return { data: undefined };
        },
        invalidatesTags: [UsersTags.CurrentUser],
      }),
      removeUser: build.mutation<void, RemoveUserRequest>({
        query: (payload) => ({
          url: '/charity/users/v1/remove',
          method: 'POST',
          data: payload,
        }),
        onQueryStarted: async ({ userId }, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            dispatch(
              usersAPISlice.util.updateQueryData('listUsers', {}, (draft) => {
                const index = draft.findIndex((u) => u.userId === userId);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              }),
            );
          } catch {
            // pass
          }
        },
      }),
    }),
  });

export default usersAPISlice;
